import { Prisma } from "@prisma/client";
import { BuyableItemEvent, BuyableItemHandler, PartialBuyableItem } from "../buyable-item.types";
import {
  EQUIPMENT_DELIVERY_FEE_GROUP_KEY_PREFIX as GROUP_KEY_PREFIX,
  EVENT_EQUIPMENT_REQUEST_GROUP_KEY_PREFIX,
} from "./equipment-group-key-prefixes";

type PartialEquipmentDeliveryFeeBuyableItem = {
  feeEventEquipmentRequest?: { id: string; event?: { id: string; name: string } | null } | null;
};

type EquipmentDeliveryFeeBuyableItem = {
  feeEventEquipmentRequest: { id: string };
};

type EquipmentDeliveryFeeWithEvent = {
  feeEventEquipmentRequest: { event: BuyableItemEvent };
};

export const EquipmentDeliveryFeeBuyableItemHandler = {
  displayName: "Frais de livraison matériel",
  accounting: {
    generalAccountCode: "7061113",
    invoiceLabel: "Frais de livraison matériel",
  },

  getEvent(item: EquipmentDeliveryFeeWithEvent): BuyableItemEvent {
    return item.feeEventEquipmentRequest.event;
  },

  isValid(item: PartialEquipmentDeliveryFeeBuyableItem): boolean {
    return !!item.feeEventEquipmentRequest?.id;
  },

  findEquivalent(
    item: PartialEquipmentDeliveryFeeBuyableItem,
  ): Prisma.BuyableItemWhereInput {
    return {
      feeEventEquipmentRequest: {
        id: item.feeEventEquipmentRequest?.id,
      },
    };
  },

  create({
    feeEventEquipmentRequest,
  }: EquipmentDeliveryFeeBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return {
      feeEventEquipmentRequest: {
        connect: {
          id: feeEventEquipmentRequest.id,
        },
      },
    };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!EquipmentDeliveryFeeBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for equipment delivery fee: ${groupKey}`);
    }

    const [, feeEventEquipmentRequestId] = groupKey.split("|");
    return {
      feeEventEquipmentRequest: {
        id: feeEventEquipmentRequestId,
      },
    };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialEquipmentDeliveryFeeBuyableItem): string {
    const requestId = item.feeEventEquipmentRequest?.id;
    if (!requestId) {
      throw new Error("Cannot get group key for invalid equipmentDeliveryFee item");
    }
    return `${GROUP_KEY_PREFIX}|${requestId}`;
  },

  getPricingQuery(item: PartialBuyableItem): Prisma.PricingFindFirstArgs {
    return {
      where: {
        eventEquipmentRequestDeliveryFee: {
          id: item.feeEventEquipmentRequest?.id,
        },
      },
    };
  },

  getLinkedGroupKeys(groupKey: string): string[] {
    const [, requestId] = groupKey.split("|");
    return [`${EVENT_EQUIPMENT_REQUEST_GROUP_KEY_PREFIX}|${requestId}`];
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      feeEventEquipmentRequest: {
        event: { name: { contains: searchText, mode: "insensitive" } },
      },
    };
  },
} as const satisfies BuyableItemHandler;
